import { ChevronDownIcon } from 'components'
import { InputWithAdornment } from 'components/Form'
import { ResultsTray } from 'components/GenericSearch/ResultsTray'
import { PhoneCall } from 'components/Icon'
import { useBoolean, useClickOutside } from 'hooks'
import { useCallback, useEffect, useRef } from 'react'
import { Box, Flex } from 'theme-ui'
import { ContactOption, TeacherContact } from 'types'
import { noop } from 'utils'
import { AdvancedInputProps } from '.'
import { BasicInputGroup } from './BasicInputGroup'

const getCurrentContact = (
  value: string | null | undefined,
  contactOptions?: TeacherContact
) => {
  if (!contactOptions) {
    return {
      phone: 'No email added',
      email: 'No phone added',
    }
  } else if (value === null) {
    return undefined
  } else if (value && contactOptions[value]) {
    return contactOptions[value]
  } else {
    return contactOptions['default']
  }
}

export interface ContactDetailsSelectProps extends AdvancedInputProps {
  value?: string | null
  contactOptions?: TeacherContact
}

export const ContactDetailsSelect = ({
  name,
  label,
  setFieldValue,
  onBlur = noop,
  contactOptions,
  disabled,
  error,
  value,
  tabIndex,
}: ContactDetailsSelectProps) => {
  const contactSelectRef = useRef<HTMLDivElement>(null)
  const [showResults, show, hide] = useBoolean(false)
  useClickOutside(contactSelectRef, hide)

  const handleItemPicked = useCallback(
    ({ id }: { id?: string }) => {
      setFieldValue(name, id)
      hide()
    },
    [setFieldValue, hide, name]
  )
  const { default: defaultContact, ...otherContacts } = contactOptions ?? {}
  //if only default contact, set the field to undefined instead of null
  useEffect(() => {
    if (Object.keys(otherContacts).length < 1 && value === null) {
      setFieldValue(name, undefined)
    }
  }, [otherContacts, value, name, setFieldValue])
  const currContact = getCurrentContact(value, contactOptions)
  const displayText = <ContactDisplay {...(currContact ?? {})} />
  const inputText = currContact
    ? `${currContact?.email ? currContact.email : ''}${
        currContact?.phone ? `, ${currContact?.phone}` : ''
      }`
    : ''

  return (
    <BasicInputGroup
      label={label}
      name={name}
      error={error}
      disabled={disabled || Object.keys(otherContacts).length < 1}
      display={displayText}
      input={
        <>
          <Box
            sx={{ position: 'relative' }}
            onBlur={onBlur}
            ref={contactSelectRef}>
            <InputWithAdornment
              id={name}
              name={name}
              value={inputText}
              aria-invalid={!!error}
              disabled={disabled}
              tabIndex={tabIndex}
              onFocus={show}
              sx={{ cursor: 'pointer', textOverflow: 'ellipsis', pr: 7 }}
              after={<ChevronDownIcon />}
              isAfterClickable={false}
              readOnly
            />
            <ResultsTray
              ItemCard={ContactSelectResult}
              variant="select"
              results={[
                { id: 'default', ...defaultContact },
                ...Object.entries(otherContacts).map(([id, option]) => ({
                  id,
                  ...option,
                })),
              ]}
              show={showResults}
              onSearchItemClick={handleItemPicked}
              noResultsText="You have not added contact details to your profile yet."
            />
          </Box>
        </>
      }
    />
  )
}

export type ContactSelectResultProps = {
  item: ContactOption & { id: string }
  onClick: (contact: ContactOption & { id: string }) => void
}

export const ContactSelectResult = ({
  item,
  onClick,
}: ContactSelectResultProps) => {
  const { email, phone } = item

  const handleClick = () => {
    if (item) onClick(item)
  }
  return (
    <Box variant="layout.result.locationSelect">
      <Flex
        sx={{ fontSize: 2, cursor: 'pointer', lineHeight: '22px' }}
        onClick={handleClick}>
        <Box sx={{ pr: 20, pt: '3px' }}>
          <PhoneCall size={24} />
        </Box>
        <Box>
          <ContactDisplay phone={phone} email={email} />
        </Box>
      </Flex>
    </Box>
  )
}

const ContactDisplay = ({
  phone,
  email,
}: {
  email?: string
  phone?: string
}) => {
  return (
    <>
      <Box sx={{ lineHeight: '24px' }}>{email}</Box>
      <Box sx={{ lineHeight: '24px' }}>{phone}</Box>
    </>
  )
}
